import classNames from 'classnames'
import { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { useIsRTL } from '@context/SessionSettingsContext'

import { CaretIcon } from '@ui/icons/Caret.icon'
import { ChevronIcon } from '@ui/icons/Chevron.icon'

import { useWindowResize } from '@utils/domUtils'

import styles from './Scrollbar.module.scss'

type Size = 'sm' | 'lg'
type ArrowDirection = 'backward' | 'forward'

interface ScrollbarProps {
  parentRef: React.RefObject<HTMLDivElement | null>
  orientation?: 'horizontal' | 'vertical'
  size?: Size
  hideOnMobile?: boolean
  isScrollableOverride?: boolean
  position?: 'inside' | 'outside'
}

const EPS = 4
const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max)

export const Scrollbar: FC<ScrollbarProps> = ({
  parentRef,
  orientation = 'vertical',
  size = 'lg',
  isScrollableOverride,
  hideOnMobile = false,
  position = 'inside',
}) => {
  const isMobile = useWindowResize()
  const rafRef = useRef<number | null>(null)

  const [thumbSize, setThumbSize] = useState(0)
  const [thumbOffset, setThumbOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartAxis, setDragStartAxis] = useState(0)
  const [dragStartThumbOffset, setDragStartThumbOffset] = useState(0)
  const [canScrollBackward, setCanScrollBackward] = useState(false)
  const [canScrollForward, setCanScrollForward] = useState(false)

  const [isScrollableAuto, setIsScrollableAuto] = useState(false)
  const [shouldMount, setShouldMount] = useState(false)
  const [isReadyToShow, setIsReadyToShow] = useState(false)

  const isScrollable = isScrollableOverride ?? isScrollableAuto

  const isScrollableRef = useRef(isScrollable)
  useEffect(() => {
    isScrollableRef.current = isScrollable
  }, [isScrollable])

  const isVertical = orientation === 'vertical'
  const isRTL = useIsRTL()

  const iconSize = 24

  const trackRef = useRef<HTMLDivElement | null>(null)
  const trackRoRef = useRef<ResizeObserver | null>(null)

  const measureTrackMain = useCallback(() => {
    const track = trackRef.current
    if (!track) return 0
    const r = track.getBoundingClientRect()
    return isVertical ? r.height : r.width
  }, [isVertical])

  const updateScrollableState = useCallback(() => {
    if (isScrollableOverride != null) return isScrollableOverride
    const parent = parentRef.current
    if (!parent) return false

    const next = isVertical
      ? parent.scrollHeight > parent.clientHeight + EPS
      : parent.scrollWidth > parent.clientWidth + EPS

    setIsScrollableAuto(next)
    return next
  }, [isScrollableOverride, isVertical, parentRef])

  const updateScrollbar = useCallback(
    (isScrollableParam?: boolean) => {
      const parent = parentRef.current
      if (!parent) return

      const effectiveIsScrollable = isScrollableParam ?? isScrollableRef.current

      const activeClass = isVertical ? 'custom-scrollbar-y-active' : 'custom-scrollbar-x-active'
      if (!parent.classList.contains(activeClass)) {
        parent.classList.add(activeClass)
      }

      if (!effectiveIsScrollable) {
        setIsReadyToShow(false)
        return
      }

      const trackMain = measureTrackMain()
      if (trackMain <= 0) {
        setIsReadyToShow(false)
        return
      }

      if (isVertical) {
        const { scrollTop, scrollHeight, clientHeight } = parent

        const newThumbHeight = (clientHeight / scrollHeight) * trackMain
        const maxScrollTop = scrollHeight - clientHeight
        const maxThumbTop = trackMain - newThumbHeight

        const clampedScrollTop = clamp(scrollTop, 0, maxScrollTop)
        const newThumbTop = maxScrollTop > 0 ? (clampedScrollTop / maxScrollTop) * maxThumbTop : 0

        setThumbSize(newThumbHeight)
        setThumbOffset(newThumbTop)
        setCanScrollBackward(clampedScrollTop > EPS)
        setCanScrollForward(clampedScrollTop < maxScrollTop - EPS)
      } else {
        const { scrollLeft, scrollWidth, clientWidth } = parent

        const newThumbWidth = (clientWidth / scrollWidth) * trackMain
        const maxScrollLeft = scrollWidth - clientWidth
        const maxThumbLeft = trackMain - newThumbWidth

        let normalizedScrollLeft = scrollLeft
        if (isRTL) {
          normalizedScrollLeft = scrollLeft <= 0 ? Math.abs(scrollLeft) : maxScrollLeft - scrollLeft
        }

        const clamped = clamp(normalizedScrollLeft, 0, maxScrollLeft)
        const newThumbLeft = maxScrollLeft > 0 ? (clamped / maxScrollLeft) * maxThumbLeft : 0

        setThumbSize(newThumbWidth)
        setThumbOffset(newThumbLeft)
        setCanScrollBackward(clamped > EPS)
        setCanScrollForward(clamped < maxScrollLeft - EPS)
      }

      setIsReadyToShow(true)
    },
    [parentRef, isVertical, measureTrackMain, isRTL]
  )

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current != null) return
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null
      const nextScrollable = updateScrollableState()
      updateScrollbar(nextScrollable)
    })
  }, [updateScrollableState, updateScrollbar])

  const setTrackRef = useCallback(
    (node: HTMLDivElement | null) => {
      trackRef.current = node

      if (trackRoRef.current) {
        trackRoRef.current.disconnect()
        trackRoRef.current = null
      }

      if (node) {
        const ro = new ResizeObserver(() => scheduleUpdate())
        ro.observe(node)
        trackRoRef.current = ro
      }
    },
    [scheduleUpdate]
  )

  useLayoutEffect(() => {
    const parent = parentRef.current
    if (!parent) return

    const activeClass = isVertical ? 'custom-scrollbar-y-active' : 'custom-scrollbar-x-active'

    setShouldMount(true)
    scheduleUpdate()

    const onScroll = () => scheduleUpdate()
    const onResize = () => scheduleUpdate()

    parent.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    const mo = new MutationObserver(() => scheduleUpdate())
    mo.observe(parent, { childList: true, subtree: true, characterData: true })

    return () => {
      if (parent) parent.classList.remove(activeClass)
      parent.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
      mo.disconnect()
      if (trackRoRef.current) {
        trackRoRef.current.disconnect()
        trackRoRef.current = null
      }
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [isVertical, parentRef, scheduleUpdate])

  useLayoutEffect(() => {
    if (!shouldMount) return
    scheduleUpdate()
  }, [shouldMount, scheduleUpdate])

  useEffect(() => {
    if (!isDragging) return

    const moveThumb = (clientPos: number) => {
      const parent = parentRef.current
      if (!parent) return

      const trackMain = measureTrackMain()
      if (trackMain <= 0) return

      if (isVertical) {
        const { scrollHeight, clientHeight } = parent
        const maxScrollTop = scrollHeight - clientHeight
        const maxThumbTop = trackMain - thumbSize

        const delta = clientPos - dragStartAxis
        const newThumbTop = clamp(dragStartThumbOffset + delta, 0, maxThumbTop)

        setThumbOffset(newThumbTop)

        const scrollRatio = maxThumbTop > 0 ? newThumbTop / maxThumbTop : 0
        parent.scrollTop = scrollRatio * maxScrollTop
      } else {
        const { scrollWidth, clientWidth } = parent
        const maxScrollLeft = scrollWidth - clientWidth
        const maxThumbLeft = trackMain - thumbSize

        const delta = isRTL ? -(clientPos - dragStartAxis) : clientPos - dragStartAxis
        const newThumbLeft = clamp(dragStartThumbOffset + delta, 0, maxThumbLeft)

        setThumbOffset(newThumbLeft)

        const scrollRatio = maxThumbLeft > 0 ? newThumbLeft / maxThumbLeft : 0
        const target = scrollRatio * maxScrollLeft

        if (isRTL) {
          const testScroll = parent.scrollLeft
          parent.scrollLeft = testScroll <= 0 ? -target : maxScrollLeft - target
        } else {
          parent.scrollLeft = target
        }
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      moveThumb(isVertical ? e.clientY : e.clientX)
    }

    const handleMouseUp = () => setIsDragging(false)

    const handleTouchMove = (e: TouchEvent) => {
      if (e.cancelable) e.preventDefault()
      moveThumb(isVertical ? e.touches[0].clientY : e.touches[0].clientX)
    }

    const handleTouchEnd = () => setIsDragging(false)

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    window.addEventListener('touchmove', handleTouchMove, { passive: false })
    window.addEventListener('touchend', handleTouchEnd)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
    }
  }, [
    isDragging,
    dragStartAxis,
    dragStartThumbOffset,
    thumbSize,
    parentRef,
    isRTL,
    isVertical,
    measureTrackMain,
  ])

  const handleDragStart = (clientPos: number) => {
    setIsDragging(true)
    setDragStartAxis(clientPos)
    setDragStartThumbOffset(thumbOffset)
  }

  const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    handleDragStart(isVertical ? e.clientY : e.clientX)
  }

  const handleThumbTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation()
    handleDragStart(isVertical ? e.touches[0].clientY : e.touches[0].clientX)
  }

  const scrollToStart = () => {
    const parent = parentRef.current
    if (!parent) return

    if (isVertical) parent.scrollTo({ top: 0, behavior: 'smooth' })
    else if (isRTL) {
      parent.scrollTo({ left: -(parent.scrollWidth - parent.clientWidth), behavior: 'smooth' })
    } else {
      parent.scrollTo({ left: 0, behavior: 'smooth' })
    }
  }

  const scrollToEnd = () => {
    const parent = parentRef.current
    if (!parent) return

    if (isVertical) parent.scrollTo({ top: parent.scrollHeight, behavior: 'smooth' })
    else if (isRTL) parent.scrollTo({ left: 0, behavior: 'smooth' })
    else parent.scrollTo({ left: parent.scrollWidth, behavior: 'smooth' })
  }

  const getArrowClickHandler = (direction: ArrowDirection) => {
    const isBackward = direction === 'backward'
    const canScroll = isBackward ? canScrollBackward : canScrollForward
    if (!canScroll) return undefined

    if (isVertical) return isBackward ? scrollToStart : scrollToEnd
    return isBackward ? (isRTL ? scrollToEnd : scrollToStart) : isRTL ? scrollToStart : scrollToEnd
  }

  const getArrowIcon = (direction: ArrowDirection, canScroll: boolean): ReactNode => {
    if (isVertical) {
      const Chevron = direction === 'backward' ? ChevronIcon.Up : ChevronIcon.Down
      return <Chevron size={iconSize} color={canScroll ? 'text-high-em' : 'text-base-em'} />
    }

    const caretDirection = direction === 'backward' ? 'left' : 'right'
    return (
      <CaretIcon
        size={iconSize}
        direction={caretDirection}
        color={canScroll ? 'text-low-em' : 'text-base-em'}
      />
    )
  }

  const renderArrow = (direction: ArrowDirection) => {
    if (size !== 'lg') return null

    const isBackward = direction === 'backward'
    const canScroll = isBackward ? canScrollBackward : canScrollForward

    return (
      <div
        className={isBackward ? styles.leftArrow : styles.rightArrow}
        onClick={getArrowClickHandler(direction)}
      >
        {getArrowIcon(direction, canScroll)}
      </div>
    )
  }

  if (!shouldMount || !isScrollable) return null

  return (
    <div
      className={classNames(
        styles.scrollbar,
        hideOnMobile && isMobile && styles.hiddenOnMobile,
        size === 'sm' && styles['size-sm'],
        {
          [styles.vertical]: isVertical,
          [styles.horizontal]: !isVertical,
          [styles.outside]: position === 'outside',
        }
      )}
      style={{
        visibility: isReadyToShow ? 'visible' : 'hidden',
        pointerEvents: isReadyToShow ? 'auto' : 'none',
      }}
    >
      {renderArrow('backward')}
      <div className={styles.scrollbarTrack} ref={setTrackRef}>
        <div
          className={styles.thumb}
          onMouseDown={handleThumbMouseDown}
          onTouchStart={handleThumbTouchStart}
          style={{
            width: isVertical ? '100%' : thumbSize,
            height: isVertical ? thumbSize : '100%',
            insetInlineStart: isVertical ? undefined : thumbOffset,
            top: isVertical ? thumbOffset : 0,
            position: 'absolute',
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
        />
      </div>
      {renderArrow('forward')}
    </div>
  )
}
